import { useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import type { ApolloError } from '@apollo/client';

import {
  AssessmentSchemaType,
  useCompletedPdfSubmissionsQuery,
  LatestCompletedProgramAssessmentsQuery,
  useLatestCompletedProgramAssessmentsQuery,
  ActivityType,
  useProviderProgramMediaQuery,
  useUploadProviderProgramMediaMutation,
  ProviderProgramMediaDataFragment,
  useDeleteProviderProgramMediaMutation,
  useClientFilesForProgramQuery,
  useExternalPartyFilesForProgramQuery,
  useProviderAssessmentsWithoutScheduleQuery,
} from '../../../../generated/graphql';

import Spinner from '../../../svgs/Spinner';

import { getTimeStamp } from '../../../lib/time';
import { logger } from '../../../../lib/logger';
import { isNetworkError } from '../../../../lib/errors';
import { ContentType } from '../../../lib/followUp';
import { defaultTransitionProps } from '../../../lib/animation';

import ContentPreview from '../../../components/ContentPreview';

import { usePatientProfileContext } from '..';

import LatestAssessmentCards from './LatestAssessmentCards';
import AssessmentsCompletedSlideover from './AssessmentsCompletedSlideover';
import EmptyProfileTab from '../../../components/EmptyProfileTab';
import IllustrationNote from '../../../svgs/IllustrationNote';
import Button from '../../../components/Button';
import {
  allAcceptedFileExtensions,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
  mediaCategoryToLabel,
} from '../../../lib/media';
import {
  DocumentIcon,
  UploadIcon,
  ClipboardListIcon,
  PaperAirplaneIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/outline';
import ThumbnailForm from '../../../svgs/ThumbnailForm';
import ThumbnailAssessment from '../../../svgs/ThumbnailAssessment';
import ThumbnailPDFESign from '../../../svgs/ThumbnailPDFESign';
import ThumbnailPdfForm from '../../../svgs/ThumbnailPdfForm';
import ThumbnailPDF from '../../../svgs/ThumbnailPDF';
import DocumentNavButton from './DocumentNavButton';
import { customToast } from '../../../components/ToastAlert/customToast';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import AddResourceModal from '../../../components/Modals/AddResourceModal';
import SendFormToExternalModal from '../../../components/Modals/SendFormToExternalModal';

const Documents = () => {
  const params = useParams();
  const programId = params.programId || '';

  const { patient } = usePatientProfileContext();
  const navigate = useNavigate();

  const [
    uploadProviderProgramMedia,
    { loading: uploadProviderProgramMediaLoading },
  ] = useUploadProviderProgramMediaMutation();
  const [
    deleteProviderProgramMedia,
    { loading: deleteProviderProgramMediaLoading },
  ] = useDeleteProviderProgramMediaMutation();

  const {
    data: latestCompletedProgramAssessmentsData,
    error: latestCompletedProgramAssessmentsError,
    loading: latestCompletedProgramAssessmentsLoading,
  } = useLatestCompletedProgramAssessmentsQuery({
    variables: {
      programId,
    },
    skip: !programId,
  });

  const {
    data: completedPdfSubmissionsData,
    error: completedPdfSubmissionsError,
    loading: completedPdfSubmissionsLoading,
  } = useCompletedPdfSubmissionsQuery({
    variables: {
      programId,
    },
    skip: !programId,
  });

  const {
    data: providerProgramMediaData,
    error: providerProgramMediaError,
    loading: providerProgramMediaLoading,
    refetch: refetchProviderProgramMedia,
  } = useProviderProgramMediaQuery({
    variables: {
      programId,
    },
    skip: !programId,
  });

  const {
    data: clientUploadedFilesData,
    error: clientUploadedFilesError,
    loading: clientUploadedFilesLoading,
  } = useClientFilesForProgramQuery({
    variables: {
      programId,
    },
    skip: !programId,
  });

  const {
    data: externalPartyFilesData,
    error: externalPartyFilesError,
    loading: externalPartyFilesLoading,
  } = useExternalPartyFilesForProgramQuery({
    variables: { programId },
    skip: !programId,
  });

  const { data: providerAssessmentsData } =
    useProviderAssessmentsWithoutScheduleQuery();
  const practitionerOnlyForms = (providerAssessmentsData?.forms ?? []).filter(
    (f) => f.completableByProviderOnly === true,
  );

  const latestCompletedProgramAssessments =
    latestCompletedProgramAssessmentsData?.latestCompletedProgramAssessments ??
    [];

  const latestCompletedStandardProgramAssessments =
    latestCompletedProgramAssessments.filter(
      (programAssessment) =>
        programAssessment.assessment.schemaType !== AssessmentSchemaType.Custom,
    );
  const latestCompletedFormProgramAssessments =
    latestCompletedProgramAssessments.filter(
      (programAssessment) =>
        programAssessment.assessment.schemaType === AssessmentSchemaType.Custom,
    );
  const externalCompletedFormProgramAssessments =
    latestCompletedFormProgramAssessments.filter(
      (pa) => pa.completedByType === 'external_party',
    );
  const clientCompletedFormProgramAssessments =
    latestCompletedFormProgramAssessments.filter(
      (pa) =>
        pa.assessment?.completableByProviderOnly !== true &&
        pa.completedByType !== 'external_party',
    );
  const practitionerCompletedFormProgramAssessments =
    latestCompletedFormProgramAssessments.filter(
      (pa) =>
        pa.assessment?.completableByProviderOnly === true &&
        pa.completedByType !== 'external_party',
    );

  const completedPdfSubmissions =
    completedPdfSubmissionsData?.completedPdfSubmissions ?? [];

  const fileSubmissions = completedPdfSubmissions.filter(
    (pdfSubmission) =>
      pdfSubmission.programActivity?.activityType === ActivityType.PdfForm,
  );
  const signedSubmissions = completedPdfSubmissions.filter(
    (pdfSubmission) =>
      pdfSubmission.programActivity?.activityType === ActivityType.Esign,
  );

  const providerProgramMedia =
    providerProgramMediaData?.patient?.providerMedia ?? [];

  const queriesLoading =
    latestCompletedProgramAssessmentsLoading ||
    completedPdfSubmissionsLoading ||
    providerProgramMediaLoading ||
    clientUploadedFilesLoading ||
    externalPartyFilesLoading;

  // Both queries have to be loaded with no errors before showing the page
  const shouldShowPage =
    !queriesLoading &&
    !latestCompletedProgramAssessmentsError &&
    !completedPdfSubmissionsError &&
    !providerProgramMediaError &&
    !clientUploadedFilesError &&
    !externalPartyFilesError;

  const clientUploadedFiles =
    clientUploadedFilesData?.clientFilesForProgram ?? [];
  const externalPartyUploadedFiles =
    externalPartyFilesData?.externalPartyFilesForProgram ?? [];

  const hasResults =
    latestCompletedProgramAssessments.length > 0 ||
    completedPdfSubmissions.length > 0 ||
    providerProgramMedia.length > 0 ||
    clientUploadedFiles.length > 0 ||
    externalPartyUploadedFiles.length > 0;

  const [
    isAssessmentsCompletedSlideoverOpen,
    setIsAssessmentsCompletedSlideoverOpen,
  ] = useState(false);

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);

  const [selectedAssessment, setSelectedAssessment] = useState<
    | LatestCompletedProgramAssessmentsQuery['latestCompletedProgramAssessments'][number]['assessment']
    | undefined
  >();
  const [selectedDocument, setSelectedDocument] =
    useState<ProviderProgramMediaDataFragment | null>(null);
  const [
    isSelectPractitionerFormModalOpen,
    setIsSelectPractitionerFormModalOpen,
  ] = useState(false);
  const [isSendFormToExternalModalOpen, setIsSendFormToExternalModalOpen] =
    useState(false);

  const [expandedSections, setExpandedSections] = useState<
    Record<'clients' | 'practitioners' | 'external', boolean>
  >({
    clients: true,
    practitioners: true,
    external: true,
  });

  const toggleSection = (section: 'clients' | 'practitioners' | 'external') => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files) return;

    // Check file size
    if (Array.from(files).some((file) => file.size > MAX_UPLOAD_SIZE_BYTES)) {
      customToast.warning(
        `File is too large. Please upload a file smaller than ${MAX_UPLOAD_SIZE_MB}MB.`,
      );
      return;
    }

    try {
      for (const file of Array.from(files)) {
        await uploadProviderProgramMedia({
          variables: {
            input: {
              programId,
              file,
              name: file.name,
            },
          },
        });
      }
      await refetchProviderProgramMedia();
      setSelectedNavItem('uploaded-files');
      customToast.success('Files uploaded successfully.');
    } catch (error) {
      // A network failure ("Failed to fetch") is a transient/environmental
      // condition (offline, server unreachable, or an upload the gateway
      // rejected — e.g. an oversized file), not an application bug. The global
      // Apollo errorLink already fires a toast for network errors, so we only
      // log here and skip a duplicate toast. See Sentry issue WEB-95.
      if (isNetworkError(error as ApolloError)) {
        logger.warn('Network error uploading files', {
          programId,
          fileCount: files?.length,
          fileNames: files ? Array.from(files).map((f) => f.name) : [],
        });
      } else {
        logger.error('Error uploading files', {
          programId,
          fileCount: files?.length,
          fileNames: files ? Array.from(files).map((f) => f.name) : [],
          error,
        });
        customToast.error('Error uploading files.');
      }
    }

    // Reset the file input after processing
    event.target.value = '';
  };

  const handleDeleteFile = async () => {
    if (!selectedDocument) {
      customToast.error('No file selected.');
      return;
    }

    try {
      await deleteProviderProgramMedia({
        variables: {
          providerProgramMediaId: selectedDocument.id,
        },
      });
      await refetchProviderProgramMedia();
      customToast.success('File deleted successfully.');
    } catch (error) {
      logger.error('Error deleting file', {
        programId,
        providerProgramMediaId: selectedDocument?.id,
        error,
      });
      customToast.error('Error deleting file.');
    }
  };

  const handleSendMailClick = () => {
    navigate(`/${patient?.programInstanceId}/follow-ups/new`);
  };

  const navItems = [
    {
      id: 'all',
      title: 'View all',
      icon: DocumentIcon,
      emptyState: {
        title: 'No forms, assessments or files yet',
        description: `Any forms or assessments you send to ${patient?.firstName} that they complete will show up here. You can also upload external files to keep everything in one place.`,
        buttonTitle: `Send mail to ${patient?.firstName}`,
        onClickButton: handleSendMailClick,
        ButtonComponent: (
          <Button
            theme="secondary"
            size="small"
            className="mt-4 px-6"
            title="Upload a file"
            onClick={handleUploadFileClick}
            disabled={uploadProviderProgramMediaLoading}
          />
        ),
      },
    },
    {
      id: 'client-completed-forms',
      title: 'Completed forms',
      section: 'clients' as const,
      emptyState: {
        title: 'No client-completed forms yet',
        description: `Forms that ${patient?.firstName} completes will show up here.`,
        buttonTitle: `Send mail to ${patient?.firstName}`,
        onClickButton: handleSendMailClick,
      },
    },
    {
      id: 'practitioner-completed-forms',
      title: 'Completed forms',
      section: 'practitioners' as const,
      emptyState: {
        title: 'No practitioner-completed forms yet',
        description: `Forms you or your team complete for ${patient?.firstName} will show up here.`,
        buttonTitle: 'Complete a form',
        onClickButton: () => setIsSelectPractitionerFormModalOpen(true),
      },
    },
    {
      id: 'external-completed-forms',
      title: 'Completed forms',
      section: 'external' as const,
      emptyState: {
        title: 'No externally completed forms yet',
        description: `Forms completed by ${
          patient?.firstName ?? 'this client'
        }'s contacts will show up here.`,
      },
    },
    {
      id: 'external-uploaded-files',
      title: 'Uploaded files',
      section: 'external' as const,
      emptyState: {
        title: 'No externally uploaded files yet',
        description: `Files that ${
          patient?.firstName ?? 'this client'
        }'s contacts upload when completing forms will show up here.`,
      },
    },
    {
      id: 'assessments',
      title: 'Assessments',
      section: 'clients' as const,
      emptyState: {
        title: 'No assessments yet',
        description: `Any assessments you send to ${patient?.firstName} that they complete will show up here.`,
        buttonTitle: `Send mail to ${patient?.firstName}`,
        onClickButton: handleSendMailClick,
      },
    },
    {
      id: 'signed-submissions',
      title: 'Signed submissions',
      section: 'clients' as const,
      emptyState: {
        title: 'No signed submissions yet',
        description: `Any E-sign PDFs you send to ${patient?.firstName} to sign will show up here.`,
        buttonTitle: `Send mail to ${patient?.firstName}`,
        onClickButton: handleSendMailClick,
      },
    },
    {
      id: 'file-submissions',
      title: 'File submissions',
      section: 'clients' as const,
      emptyState: {
        title: 'No file submissions yet',
        description: `Any files you send to ${patient?.firstName} to fill out & return will show up here.`,
        buttonTitle: `Send mail to ${patient?.firstName}`,
        onClickButton: handleSendMailClick,
      },
    },
    {
      id: 'uploaded-files',
      title: 'Uploaded files',
      section: 'practitioners' as const,
      emptyState: {
        title: 'No uploaded files yet',
        description: `Upload ${patient?.firstName}'s external files here to keep everything in one place. These will only be visible to you and your team.`,
        buttonTitle: 'Upload a file',
        onClickButton: handleUploadFileClick,
      },
    },
    {
      id: 'client-uploaded',
      title: 'Uploaded files',
      section: 'clients' as const,
      emptyState: {
        title: 'No client uploaded files yet',
        description: `Files that ${patient?.firstName} uploads directly will appear here.`,
      },
    },
  ];

  const [selectedNavItem, setSelectedNavItem] = useState(navItems[0].id);

  const renderEmptyState = (navItem: typeof navItems[number]) => (
    <EmptyProfileTab
      IconComponent={IllustrationNote}
      iconClassName={'text-secondary-100'}
      titleText={navItem.emptyState.title}
      subTitleText={navItem.emptyState.description}
      buttonTitle={navItem.emptyState.buttonTitle}
      onClickButton={navItem.emptyState.onClickButton}
      ButtonComponent={navItem.emptyState.ButtonComponent}
    />
  );

  const renderContent = () => {
    const navItem = navItems.find((item) => item.id === selectedNavItem);
    if (!navItem) return null;

    switch (navItem.id) {
      case 'all':
        if (!hasResults) {
          return renderEmptyState(navItem);
        }
        const hasClientContent =
          clientCompletedFormProgramAssessments.length > 0 ||
          latestCompletedStandardProgramAssessments.length > 0 ||
          signedSubmissions.length > 0 ||
          fileSubmissions.length > 0 ||
          clientUploadedFiles.length > 0;
        const hasPractitionerContent =
          practitionerCompletedFormProgramAssessments.length > 0 ||
          providerProgramMedia.length > 0;
        const hasExternalContent =
          externalCompletedFormProgramAssessments.length > 0 ||
          externalPartyUploadedFiles.length > 0;

        const sectionConfig: Array<{
          key: 'clients' | 'practitioners' | 'external';
          label: string;
          hasContent: boolean;
          content: ReactNode;
        }> = [
          {
            key: 'clients',
            label: `${patient?.firstName ?? 'Client'}'s documents & files`,
            hasContent: hasClientContent,
            content: (
              <>
                {renderClientCompletedForms(true)}
                {renderAssessments(true)}
                {renderSignedSubmissions(true)}
                {renderFileSubmissions(true)}
                {renderClientUploadedFiles(true)}
              </>
            ),
          },
          {
            key: 'practitioners',
            label: 'Documents & files from team members',
            hasContent: hasPractitionerContent,
            content: (
              <>
                {renderPractitionerCompletedForms(true)}
                {renderUploadedFiles(true)}
              </>
            ),
          },
          {
            key: 'external',
            label: `Documents & files from ${
              patient?.firstName ?? 'this client'
            }'s contacts`,
            hasContent: hasExternalContent,
            content: (
              <>
                {renderExternalCompletedForms(true)}
                {renderExternalUploadedFiles(true)}
              </>
            ),
          },
        ];

        return (
          <div className="flex flex-col gap-y-8">
            {sectionConfig.map(
              ({ key, label, hasContent, content }) =>
                hasContent && (
                  <div key={key} className="flex flex-col gap-y-3">
                    <button
                      type="button"
                      onClick={() => toggleSection(key)}
                      className="flex w-full items-center gap-2 text-left"
                    >
                      {expandedSections[key] ? (
                        <ChevronUpIcon className="h-5 w-5 shrink-0 text-neutral-200" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 shrink-0 text-neutral-200" />
                      )}
                      <span className="text-category font-medium tracking-wide text-neutral-200">
                        {label}
                      </span>
                      <div className="h-px flex-1 bg-neutral-75" />
                    </button>
                    {expandedSections[key] && (
                      <div className="flex flex-col gap-y-10">{content}</div>
                    )}
                  </div>
                ),
            )}
          </div>
        );
      case 'client-completed-forms':
        return clientCompletedFormProgramAssessments.length
          ? renderClientCompletedForms(true)
          : renderEmptyState(navItem);
      case 'practitioner-completed-forms':
        return practitionerCompletedFormProgramAssessments.length
          ? renderPractitionerCompletedForms(true)
          : renderEmptyState(navItem);
      case 'external-completed-forms':
        return externalCompletedFormProgramAssessments.length
          ? renderExternalCompletedForms(true)
          : renderEmptyState(navItem);
      case 'external-uploaded-files':
        return externalPartyUploadedFiles.length
          ? renderExternalUploadedFiles(true)
          : renderEmptyState(navItem);
      case 'assessments':
        return latestCompletedStandardProgramAssessments.length
          ? renderAssessments(true)
          : renderEmptyState(navItem);
      case 'signed-submissions':
        return signedSubmissions.length
          ? renderSignedSubmissions(true)
          : renderEmptyState(navItem);
      case 'file-submissions':
        return fileSubmissions.length
          ? renderFileSubmissions(true)
          : renderEmptyState(navItem);
      case 'uploaded-files':
        return providerProgramMedia.length
          ? renderUploadedFiles(true)
          : renderEmptyState(navItem);
      case 'client-uploaded':
        return clientUploadedFiles.length
          ? renderClientUploadedFiles(true)
          : renderEmptyState(navItem);
      default:
        return null;
    }
  };

  const renderClientCompletedForms = (showTitle = false) => {
    if (clientCompletedFormProgramAssessments.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailForm className="h-6 w-6 shrink-0 rounded-md" />
            Forms completed by {patient?.firstName}
          </div>
        )}
        <LatestAssessmentCards
          hideThumbnail={showTitle}
          patientFirstName={patient?.firstName}
          latestCompletedProgramAssessments={
            clientCompletedFormProgramAssessments
          }
          setSelectedAssessment={setSelectedAssessment}
          setIsAssessmentsCompletedSlideoverOpen={
            setIsAssessmentsCompletedSlideoverOpen
          }
        />
      </div>
    );
  };

  const renderPractitionerCompletedForms = (showTitle = false) => {
    if (practitionerCompletedFormProgramAssessments.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailForm
              className="h-6 w-6 shrink-0 rounded-md"
              isProviderOnly
            />
            Forms completed by team members
          </div>
        )}
        <LatestAssessmentCards
          hideThumbnail={showTitle}
          patientFirstName={patient?.firstName}
          latestCompletedProgramAssessments={
            practitionerCompletedFormProgramAssessments
          }
          setSelectedAssessment={setSelectedAssessment}
          setIsAssessmentsCompletedSlideoverOpen={
            setIsAssessmentsCompletedSlideoverOpen
          }
        />
      </div>
    );
  };

  const renderExternalCompletedForms = (showTitle = false) => {
    if (externalCompletedFormProgramAssessments.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailForm
              className="h-6 w-6 shrink-0 rounded-md"
              isProviderOnly
            />
            Forms completed by Contacts
          </div>
        )}
        <LatestAssessmentCards
          hideThumbnail={showTitle}
          patientFirstName={patient?.firstName}
          latestCompletedProgramAssessments={
            externalCompletedFormProgramAssessments
          }
          setSelectedAssessment={setSelectedAssessment}
          setIsAssessmentsCompletedSlideoverOpen={
            setIsAssessmentsCompletedSlideoverOpen
          }
        />
      </div>
    );
  };

  const renderAssessments = (showTitle = false) => {
    if (latestCompletedStandardProgramAssessments.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailAssessment className="h-6 w-6 shrink-0 rounded-md" />
            Assessments
          </div>
        )}
        <LatestAssessmentCards
          hideThumbnail={showTitle}
          patientFirstName={patient?.firstName}
          latestCompletedProgramAssessments={
            latestCompletedStandardProgramAssessments
          }
          setSelectedAssessment={setSelectedAssessment}
          setIsAssessmentsCompletedSlideoverOpen={
            setIsAssessmentsCompletedSlideoverOpen
          }
        />
      </div>
    );
  };

  const renderSignedSubmissions = (showTitle = false) => {
    if (signedSubmissions.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailPDFESign className="h-6 w-6 shrink-0 rounded-md" />
            Signed submissions
          </div>
        )}
        <div className="mt-6 flex w-full flex-wrap gap-7">
          {signedSubmissions?.map((pdfSubmission, i) => (
            <ContentPreview
              key={`signedSubmissionContentPreview_${i}`}
              contentType={ContentType.Esign}
              className="max-w-[284px] !p-3"
              hideThumbnail={showTitle}
              title={pdfSubmission.programActivity.name}
              description={`Signed ${getTimeStamp(
                pdfSubmission.signedAt,
                false,
              )}`}
              onClick={() => {
                window.open(
                  pdfSubmission?.media?.url,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderFileSubmissions = (showTitle = false) => {
    if (fileSubmissions.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailPdfForm className="h-6 w-6 shrink-0 rounded-md" />
            File submissions
          </div>
        )}
        <div className="mt-6 flex w-full flex-wrap gap-7">
          {fileSubmissions?.map((pdfSubmission, i) => (
            <ContentPreview
              key={`fileSubmissionContentPreview_${i}`}
              contentType={ContentType.PdfForm}
              className="max-w-[284px] !p-3"
              hideThumbnail={showTitle}
              title={pdfSubmission.programActivity.name}
              description={`Completed ${getTimeStamp(
                pdfSubmission.programActivity?.completedAt,
                false,
              )}`}
              onClick={() => {
                window.open(
                  pdfSubmission?.media?.url,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderUploadedFiles = (showTitle = false) => {
    if (providerProgramMedia.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailPDF className="h-6 w-6 shrink-0 rounded-md" />
            Files uploaded by team members
          </div>
        )}
        <div className="mt-6 flex w-full flex-wrap gap-7">
          {providerProgramMedia?.map((providerProgramMedia, i) => (
            <ContentPreview
              key={`uploadedFileContentPreview_${i}`}
              contentType={ContentType.PDF}
              className="max-w-[284px] !p-3"
              hideThumbnail={showTitle}
              title={providerProgramMedia.name}
              uploadedByLabel={
                providerProgramMedia.createdByProviderUser?.name ??
                'Practitioner'
              }
              uploadedDate={getTimeStamp(providerProgramMedia.createdAt, false)}
              label={mediaCategoryToLabel[providerProgramMedia.media.category]}
              onClick={() => {
                window.open(
                  providerProgramMedia.media.url,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              onClickCloseX={() => {
                setSelectedDocument(providerProgramMedia);
                setIsConfirmDeleteModalOpen(true);
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderClientUploadedFiles = (showTitle = false) => {
    if (clientUploadedFiles.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailPDF className="h-6 w-6 shrink-0 rounded-md" />
            Files uploaded by {patient?.firstName} ({clientUploadedFiles.length}
            )
          </div>
        )}
        <div className="mt-6 flex w-full flex-wrap gap-7">
          {clientUploadedFiles?.map((file, i) => (
            <ContentPreview
              key={`clientUploadedFileContentPreview_${i}`}
              contentType={ContentType.PDF}
              className="max-w-[284px] !p-3"
              hideThumbnail={showTitle}
              title={file.name}
              uploadedByLabel={patient?.firstName ?? 'Client'}
              uploadedDate={getTimeStamp(file.createdAt, false)}
              onClick={() => {
                window.open(file.media.url, '_blank', 'noopener,noreferrer');
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderExternalUploadedFiles = (showTitle = false) => {
    if (externalPartyUploadedFiles.length === 0) return null;

    return (
      <div>
        {showTitle && (
          <div className="text-xs flex items-center gap-2 font-medium text-green-150">
            <ThumbnailPDF className="h-6 w-6 shrink-0 rounded-md" />
            Files uploaded by Contacts ({externalPartyUploadedFiles.length})
          </div>
        )}
        <div className="mt-6 flex w-full flex-wrap gap-7">
          {externalPartyUploadedFiles.map((file, i) => (
            <ContentPreview
              key={`externalUploadedFileContentPreview_${i}`}
              contentType={ContentType.PDF}
              className="max-w-[284px] !p-3"
              hideThumbnail={showTitle}
              title={file.name}
              uploadedByLabel={file.externalCompleterName ?? 'External'}
              uploadedDate={getTimeStamp(file.createdAt, false)}
              onClick={() => {
                window.open(file.media.url, '_blank', 'noopener,noreferrer');
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Transition
        show={shouldShowPage}
        {...defaultTransitionProps}
        className="flex h-full w-full flex-grow flex-row items-start"
      >
        <div className="flex h-full w-[13.125rem] shrink-0 grow-0 flex-col border-r border-neutral-75">
          <div className="flex h-full w-full flex-col gap-y-2 overflow-y-auto overscroll-y-contain py-4 pr-4">
            {navItems
              .filter((item) => item.section == null)
              .map((item) => (
                <DocumentNavButton
                  key={item.id}
                  title={item.title}
                  isSelected={selectedNavItem === item.id}
                  onClick={() => setSelectedNavItem(item.id)}
                  IconComponent={item.icon}
                />
              ))}
            {(['clients', 'practitioners', 'external'] as const).map(
              (section) => {
                const sectionItems = navItems.filter(
                  (item) => item.section === section,
                );
                if (sectionItems.length === 0) return null;
                return (
                  <div key={section} className="flex flex-col gap-y-2">
                    <div className="flex items-center gap-2 pt-2">
                      <span className="text-xs font-medium tracking-wide text-neutral-200">
                        {section === 'clients'
                          ? 'Client'
                          : section === 'practitioners'
                          ? 'Team member'
                          : 'Contact'}
                      </span>
                      <div className="h-px flex-1 bg-neutral-75" />
                    </div>
                    {sectionItems.map((item) => (
                      <DocumentNavButton
                        key={item.id}
                        title={item.title}
                        isSelected={selectedNavItem === item.id}
                        onClick={() => setSelectedNavItem(item.id)}
                        IconComponent={item.icon}
                      />
                    ))}
                  </div>
                );
              },
            )}
          </div>
        </div>
        <div className="flex h-full w-full flex-col gap-y-10 overflow-y-auto px-5 pb-10">
          <div className="sticky top-0 z-10 -mx-5 flex flex-row flex-wrap items-center justify-start gap-3 bg-white px-5 py-3">
            <Button
              size="small"
              IconComponent={
                uploadProviderProgramMediaLoading ? Spinner : UploadIcon
              }
              title="Upload a file"
              iconClassName="h-5 w-5 shrink-0"
              onClick={handleUploadFileClick}
              disabled={uploadProviderProgramMediaLoading}
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept={allAcceptedFileExtensions.join(',')}
              multiple
            />
            {practitionerOnlyForms.length > 0 && (
              <>
                <Button
                  size="small"
                  IconComponent={ClipboardListIcon}
                  title="Complete a form"
                  iconClassName="h-5 w-5 shrink-0"
                  onClick={() => setIsSelectPractitionerFormModalOpen(true)}
                />
                <Button
                  size="small"
                  IconComponent={PaperAirplaneIcon}
                  title="Send a form to a Contact"
                  iconClassName="h-5 w-5 shrink-0"
                  onClick={() => setIsSendFormToExternalModalOpen(true)}
                />
              </>
            )}
          </div>
          {renderContent()}
        </div>
      </Transition>

      <AssessmentsCompletedSlideover
        onClose={() => setIsAssessmentsCompletedSlideoverOpen(false)}
        isOpen={isAssessmentsCompletedSlideoverOpen}
        patientFirstName={`${patient?.firstName}`}
        programId={patient?.programInstanceId}
        selectedAssessment={selectedAssessment}
      />

      <ConfirmDeleteModal
        isOpen={isConfirmDeleteModalOpen}
        setClosed={() => {
          setIsConfirmDeleteModalOpen(false);
          setSelectedDocument(null);
        }}
        title="Are you sure you want to delete this file?"
        actionButtonTitle="Delete"
        performDelete={handleDeleteFile}
        fetching={deleteProviderProgramMediaLoading}
      />

      <AddResourceModal
        isModalOpen={isSelectPractitionerFormModalOpen}
        setClosed={() => setIsSelectPractitionerFormModalOpen(false)}
        practitionerOnlyFormsOnly
        contentTypeFilterLock={ContentType.Form}
        onSelectPractitionerForm={(assessmentId) => {
          if (programId) {
            navigate(
              `/complete-practitioner-form?assessmentId=${assessmentId}&programId=${programId}`,
            );
          }
          setIsSelectPractitionerFormModalOpen(false);
        }}
      />

      <SendFormToExternalModal
        isOpen={isSendFormToExternalModalOpen}
        setClosed={() => setIsSendFormToExternalModalOpen(false)}
        programId={programId}
        patientFirstName={patient?.firstName ?? undefined}
      />

      {!shouldShowPage && <Spinner className="mx-auto mt-8" />}
    </>
  );
};

export default Documents;
